import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchProfileActivities, saveProfileActivity } from '../../studyConfigurationApi';

const emptyDraft = (projectCode, profileNo, profileCode) => ({
  projectCode,
  profileActivityMappingNo: null,
  profileNo: profileNo ?? 0,
  profileCode: profileCode ?? '',
  scheduleModify: 0,
  visitActivity: 0,
  startReport: 0,
  approveSendback: 0,
  canRaiseDcf: 0,
  canRaiseActionItem: 0,
  isViewOnly: 0,
});

const FLAGS = [
  { key: 'scheduleModify', label: 'Schedule Modify' },
  { key: 'visitActivity', label: 'Visit Activity' },
  { key: 'startReport', label: 'Start Report' },
  { key: 'approveSendback', label: 'Approve/Sendback' },
  { key: 'canRaiseDcf', label: 'Raise DCF' },
  { key: 'canRaiseActionItem', label: 'Raise Action Item' },
  { key: 'isViewOnly', label: 'View Only' },
];

const ProfileActivitiesTab = ({ projectCode, profiles }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    if (!projectCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProfileActivities(projectCode);
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load profile permissions.');
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => { load(); }, [load]);

  const mappedProfileNos = useMemo(
    () => new Set(rows.map((r) => r.profileNo)),
    [rows]
  );

  const unmappedProfiles = useMemo(
    () => (profiles || []).filter((p) => !mappedProfileNos.has(p.profileNo)),
    [profiles, mappedProfileNos]
  );

  const startEdit = (row) => {
    setEditingKey(row.profileActivityMappingNo);
    setDraft({
      projectCode,
      profileActivityMappingNo: row.profileActivityMappingNo,
      profileNo: row.profileNo,
      profileCode: row.profileCode,
      scheduleModify: row.scheduleModify,
      visitActivity: row.visitActivity,
      startReport: row.startReport,
      approveSendback: row.approveSendback,
      canRaiseDcf: row.canRaiseDcf,
      canRaiseActionItem: row.canRaiseActionItem,
      isViewOnly: row.isViewOnly,
    });
    setError(null);
  };

  const startNew = () => {
    const p = unmappedProfiles[0];
    if (!p) return;
    setEditingKey('new');
    setDraft(emptyDraft(projectCode, p.profileNo, p.profileCode));
    setError(null);
  };

  const updateDraftProfile = (profileNo) => {
    const p = profiles.find((x) => x.profileNo === Number(profileNo));
    setDraft((prev) => ({
      ...prev,
      profileNo: Number(profileNo),
      profileCode: p?.profileCode ?? prev.profileCode,
    }));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const updateDraftFlag = (key, checked) => {
    setDraft((prev) => ({ ...prev, [key]: checked ? 1 : 0 }));
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await saveProfileActivity(draft);
      setEditingKey(null);
      setDraft(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = (row) =>
    editingKey != null && editingKey === row.profileActivityMappingNo;

  const renderFlagCell = (row, flagKey, editable) => {
    const value = editable && draft ? draft[flagKey] : row[flagKey];
    return (
      <td className="sc-flag-cell">
        <input
          type="checkbox"
          checked={!!value}
          disabled={!editable || saving}
          onChange={(e) => editable && updateDraftFlag(flagKey, e.target.checked)}
          aria-label={FLAGS.find((f) => f.key === flagKey)?.label}
        />
      </td>
    );
  };

  const renderProfileCell = (row, editable, isNew) => {
    if (editable && draft) {
      const options = isNew ? unmappedProfiles : [{ profileNo: draft.profileNo, profileCode: draft.profileCode }];
      return (
        <td>
          <select
            value={draft.profileNo}
            disabled={!isNew || saving}
            onChange={(e) => isNew && updateDraftProfile(e.target.value)}
            aria-label="Profile"
          >
            {options.map((p) => (
              <option key={p.profileNo} value={p.profileNo}>{p.profileCode}</option>
            ))}
          </select>
        </td>
      );
    }
    return <td>{row.profileCode ?? '—'}</td>;
  };

  const renderActionsCell = (row, editable) => {
    if (editable) {
      return (
        <td className="sc-actions-cell">
          <button
            type="button"
            className="sc-btn sc-btn-primary sc-btn-sm"
            disabled={saving}
            onClick={handleSave}
          >
            Save
          </button>
          <button
            type="button"
            className="sc-btn sc-btn-secondary sc-btn-sm"
            disabled={saving}
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </td>
      );
    }
    return (
      <td className="sc-actions-cell">
        <button
          type="button"
          className="sc-btn sc-btn-link"
          disabled={editingKey != null}
          onClick={() => startEdit(row)}
        >
          Edit
        </button>
      </td>
    );
  };

  if (!projectCode) {
    return (
      <p className="sc-empty">
        No project in session. Change project from Visit or Site pages (shown in the header).
      </p>
    );
  }
  if (loading) return <ScSpinner />;

  const showNewRow = unmappedProfiles.length > 0;
  const editingNew = editingKey === 'new';

  return (
    <div className="sc-profile-activities">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}

      <div className="sc-grid-wrap">
        <table className="sc-grid sc-flag-grid">
          <thead>
            <tr>
              <th>Profile</th>
              {FLAGS.map((f) => (
                <th key={f.key}>{f.label}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const editable = isEditing(row);
              return (
                <tr key={row.profileActivityMappingNo} className={editable ? 'sc-row-editing' : ''}>
                  {renderProfileCell(row, editable, false)}
                  {FLAGS.map((f) => renderFlagCell(row, f.key, editable))}
                  {renderActionsCell(row, editable)}
                </tr>
              );
            })}

            {showNewRow && (
              <tr className={editingNew ? 'sc-row-editing sc-row-new' : 'sc-row-new'}>
                {editingNew && draft ? (
                  <>
                    {renderProfileCell(draft, true, true)}
                    {FLAGS.map((f) => renderFlagCell(draft, f.key, true))}
                    {renderActionsCell(draft, true)}
                  </>
                ) : (
                  <>
                    <td>—</td>
                    {FLAGS.map((f) => (
                      <td key={f.key} className="sc-flag-cell">
                        <input type="checkbox" disabled checked={false} aria-label={f.label} />
                      </td>
                    ))}
                    <td className="sc-actions-cell">
                      <button
                        type="button"
                        className="sc-btn sc-btn-link"
                        disabled={editingKey != null}
                        onClick={startNew}
                      >
                        New
                      </button>
                    </td>
                  </>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !showNewRow && (
        <p className="sc-empty">No profile permissions configured. Use Apply Default Configuration first.</p>
      )}
    </div>
  );
};

export default ProfileActivitiesTab;
